/**
 * Search facets for the result surface (search-result-disclosure 02).
 *
 * Two jobs, both pure and shared by the row rendering and the filter panel:
 *
 * 1. **Selection** — values inside one property are OR-ed, properties are
 *    AND-ed: `(owner:Anna OR owner:Ben) AND (tag:camping)`.
 * 2. **Options** — a facet is computed against the *other* active filters, so a
 *    value that would yield no hit is omitted and its count reflects the other
 *    filters. Selected values are re-added with count 0 so they can always be
 *    deselected (ADR-0022 amendment: an item's owner/tags are its placement
 *    container's).
 *
 * The module knows nothing about containers or items — callers reduce each hit
 * to a `FacetHit`. That keeps the semantics testable and lets the search row,
 * the filter panel and any future surface share one implementation.
 */

/** The facet-relevant shape of one search hit. */
export interface FacetHit {
  /** Empty string means unassigned. */
  owner: string;
  tags: readonly string[];
  /** Container kind or item category — the documented "type" ambiguity. */
  type: string;
}

export interface SearchFacetState {
  owners: string[];
  tags: string[];
  types: string[];
}

export const EMPTY_SEARCH_FACETS: SearchFacetState = { owners: [], tags: [], types: [] };

/** Display switches (search-result-disclosure 04). */
export interface SearchDisplayState {
  previews: boolean;
  descriptions: boolean;
}

export const DEFAULT_SEARCH_DISPLAY: SearchDisplayState = {
  previews: true,
  descriptions: true,
};

export interface FacetOption {
  value: string;
  count: number;
}

export interface FacetOptions {
  owners: FacetOption[];
  tags: FacetOption[];
  types: FacetOption[];
}

/** Toggle one value in a facet array without mutating it. */
export function toggleFacetValue(values: readonly string[], value: string): string[] {
  return values.includes(value)
    ? values.filter((entry) => entry !== value)
    : [...values, value];
}

/** True when a hit passes every selected facet (OR inside, AND across). */
export function matchesFacets(hit: FacetHit, facets: SearchFacetState): boolean {
  const ownerOk = facets.owners.length === 0 || facets.owners.includes(hit.owner);
  const tagOk = facets.tags.length === 0 || hit.tags.some((tag) => facets.tags.includes(tag));
  const typeOk = facets.types.length === 0 || facets.types.includes(hit.type);
  return ownerOk && tagOk && typeOk;
}

/** Keep the hits passing every selected facet. */
export function applyFacets<T extends FacetHit>(
  hits: readonly T[],
  facets: SearchFacetState,
): T[] {
  return hits.filter((hit) => matchesFacets(hit, facets));
}

/**
 * Facet values with contextual counts: each facet is computed from the hits
 * that survive the *other* facets, unavailable values are omitted, and the
 * facet's own selected values are always present (count 0 when empty).
 */
export function availableFacetOptions<T extends FacetHit>(
  hits: readonly T[],
  facets: SearchFacetState,
): FacetOptions {
  return {
    owners: optionsOf(
      hits
        .filter((hit) => matchesExcept(hit, facets, "owners"))
        .map((hit) => hit.owner),
      facets.owners,
    ),
    tags: optionsOf(
      hits
        .filter((hit) => matchesExcept(hit, facets, "tags"))
        .flatMap((hit) => hit.tags),
      facets.tags,
    ),
    types: optionsOf(
      hits
        .filter((hit) => matchesExcept(hit, facets, "types"))
        .map((hit) => hit.type),
      facets.types,
    ),
  };
}

/** Turns one stored facet value into its display label (default: as stored). */
export type FacetValueLabel = (facet: keyof SearchFacetState, value: string) => string;

/**
 * The applied filters as one boolean expression, values OR-ed inside a property
 * and properties AND-ed — `(owner:Anna OR owner:Ben) AND tag:camping`.
 *
 * `formatValue` lets a surface translate item categories without changing how
 * owners and tags (and other callers) print by default.
 */
export function describeFacets(
  facets: SearchFacetState,
  formatValue: FacetValueLabel = (_facet, value) => value,
): string {
  const groups: string[] = [];
  const addGroup = (facet: keyof SearchFacetState, key: string) => {
    const values = facets[facet];
    if (values.length === 0) return;
    const terms = values.map(
      (value) => `${key}:${formatValue(facet, value) || "unassigned"}`,
    );
    groups.push(terms.length === 1 ? terms[0]! : `(${terms.join(" OR ")})`);
  };
  addGroup("owners", "owner");
  addGroup("tags", "tag");
  addGroup("types", "type");
  return groups.join(" AND ");
}

/** Does the hit survive every facet except `except`? */
function matchesExcept<T extends FacetHit>(
  hit: T,
  facets: SearchFacetState,
  except: keyof SearchFacetState,
): boolean {
  const ownerOk =
    except === "owners" || facets.owners.length === 0 || facets.owners.includes(hit.owner);
  const tagOk =
    except === "tags" ||
    facets.tags.length === 0 ||
    hit.tags.some((tag) => facets.tags.includes(tag));
  const typeOk =
    except === "types" || facets.types.length === 0 || facets.types.includes(hit.type);
  return ownerOk && tagOk && typeOk;
}

/** Counts for one facet plus any selected value the other facets removed. */
function optionsOf(values: readonly string[], selected: readonly string[]): FacetOption[] {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  for (const value of selected) {
    if (!counts.has(value)) counts.set(value, 0);
  }
  return [...counts.entries()]
    .sort(([left], [right]) => compareValues(left, right))
    .map(([value, count]) => ({ value, count }));
}

/** Unassigned last, everything else alphabetically. */
function compareValues(left: string, right: string): number {
  if (left === right) return 0;
  if (left === "") return 1;
  if (right === "") return -1;
  return left.localeCompare(right);
}
